import { Component } from '@theme/component';

/**
 * The Bricks desktop menu.
 *
 * Separate from Horizon's `header-menu.js` so the stock menu keeps working and
 * Theme settings can switch between the two.
 *
 * The panel opens on hover and on click. A hover-opened panel closes shortly
 * after the pointer leaves, which keeps the menu out of the way; a clicked one
 * pins itself open until the next click outside or Escape, so the panel can be
 * read without keeping the pointer inside it.
 *
 * The panel is fixed rather than absolutely positioned, because it spans the
 * viewport while its trigger sits inside the header's left column. That means
 * its top edge has to be measured from the header itself.
 *
 * @extends {Component}
 */
class HeaderMenuBricks extends Component {
  /** How long a hover-opened panel survives after the pointer leaves. */
  static closeDelay = 220;

  /**
   * The gap between the header's bottom edge and the panel. Zero: the panel
   * starts exactly where the header ends, so it never covers the menu links.
   * The hover bridge below spans whatever distance is left.
   */
  static panelOffset = 0;

  /** @type {HTMLElement | null} */
  #openItem = null;

  /** @type {boolean} Whether the open panel was pinned by a click. */
  #pinned = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** @type {number | null} RAF id, so scrolling repositions at most once a frame. */
  #positionFrame = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    document.addEventListener('click', this.#onDocumentClick);
    window.addEventListener('resize', this.#schedulePosition, { passive: true });
    window.addEventListener('scroll', this.#schedulePosition, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    clearTimeout(this.#closeTimer);

    if (this.#positionFrame !== null) cancelAnimationFrame(this.#positionFrame);

    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener('click', this.#onDocumentClick);
    window.removeEventListener('resize', this.#schedulePosition);
    window.removeEventListener('scroll', this.#schedulePosition);
  }

  /**
   * Open the panel belonging to the hovered or focused item.
   * @param {Event} [event]
   */
  open(event) {
    const item = this.#itemFrom(event);

    if (!item) return;

    clearTimeout(this.#closeTimer);

    if (item === this.#openItem) return;

    this.#closeAll();
    item.classList.add('is-open');
    item.querySelector('.bricks-menu__link')?.setAttribute('aria-expanded', 'true');
    this.#openItem = item;
    this.#position();
  }

  /**
   * Open on click and keep it open, so the panel can be used without hovering.
   * The parent link never navigates: the panel carries its own links.
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();

    const item = this.#itemFrom(event);

    if (!item) return;

    if (item === this.#openItem && this.#pinned) {
      this.#closeAll();
      return;
    }

    this.open(event);
    this.#pinned = true;
  }

  /**
   * Close after a short grace period, unless a click pinned the panel open.
   * @param {Event} [event]
   */
  close(event) {
    if (this.#pinned) return;

    const item = this.#itemFrom(event);

    if (!item) return;

    const next = /** @type {FocusEvent} */ (event)?.relatedTarget;

    if (next instanceof Node && item.contains(next)) return;

    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.#closeAll(), HeaderMenuBricks.closeDelay);
  }

  /**
   * @param {Event} [event]
   * @returns {HTMLElement | null}
   */
  #itemFrom(event) {
    const target = event?.target;

    if (!(target instanceof Element)) return null;

    return target.closest('.bricks-menu__item');
  }

  #closeAll = () => {
    clearTimeout(this.#closeTimer);

    for (const item of this.querySelectorAll('.bricks-menu__item.is-open')) {
      item.classList.remove('is-open');
      item.querySelector('.bricks-menu__link')?.setAttribute('aria-expanded', 'false');
    }

    this.#openItem = null;
    this.#pinned = false;
  };

  /**
   * Any click that lands outside the open item closes the panel.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#openItem) return;

    const target = event.target;

    if (target instanceof Node && this.#openItem.contains(target)) return;

    this.#closeAll();
  };

  /**
   * Scrolling fires far more often than the page paints, and the header moves
   * with the scroll, so measure once per frame instead of once per event.
   */
  #schedulePosition = () => {
    if (!this.#openItem || this.#positionFrame !== null) return;

    this.#positionFrame = requestAnimationFrame(() => {
      this.#positionFrame = null;
      this.#position();
    });
  };

  /**
   * Park the panel just under the header, wherever the header currently sits.
   */
  #position = () => {
    if (!this.#openItem) return;

    const header = this.closest('#header-component') ?? this.closest('header') ?? this.closest('.header-section');
    const bottom = header?.getBoundingClientRect().bottom ?? 100;

    // A non-sticky header scrolls away entirely. Once it has, the panel has
    // nothing left to hang from, so close it rather than pin it to the top.
    if (bottom <= 0) {
      this.#closeAll();
      return;
    }

    const top = Math.max(0, Math.round(bottom) - HeaderMenuBricks.panelOffset);

    this.style.setProperty('--bmenu-panel-top', `${top}px`);

    // The hover bridge has to span whatever is left between the link and the
    // panel, which depends on how tall the header happens to be.
    const itemBottom = this.#openItem.getBoundingClientRect().bottom;

    this.style.setProperty('--bmenu-bridge', `${Math.max(0, Math.round(top - itemBottom))}px`);
  };

  /** @param {KeyboardEvent} event */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#closeAll();
  };
}

if (!customElements.get('header-menu-bricks')) {
  customElements.define('header-menu-bricks', HeaderMenuBricks);
}
